import { Activity, AlertTriangle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../foundation/altzero_base/base/components/ui/card';
import { cn } from '../../../foundation/altzero_base/base/lib/utils';

// "Are mondo AND powermetric still sending us files?"
//
// The dashboard's DataFreshness caption answers only "did something arrive
// recently". It cannot answer this one: mondo delivers ~3 files/day and
// powermetric 30-90, so mondo going silent for a week leaves the newest-row
// timestamp looking perfectly healthy.
//
// Each cell carries its FILE COUNT, not just a colour. Colour alone says
// "something is wrong" without saying how wrong, and 3-vs-0 for mondo and
// 90-vs-30 for powermetric are different sizes of wrong.

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3001';

/** One row of the bv_gcs_daily_ingest view (migration 098). */
export type DailyIngestRow = {
  day: string;
  provider: string;
  files_ok: number;
  files_err: number;
  rows_inserted: number;
  bytes_ingested: number;
  last_at: string | null;
};

// Providers are hardcoded in gcsService (the literal union 'mondo' |
// 'powermetric') and in the view's CROSS JOIN. Listing them here too keeps row
// order stable and means a provider returning no rows at all still gets a row
// of zeros rather than vanishing from the table.
export const PROVIDERS = ['mondo', 'powermetric'] as const;

// How many consecutive silent days before the banner fires.
//
// One missed day is normal — providers skip a day, a batch runs late. Prod's
// mondo feed sent nothing on 2026-08-20 and resumed the next morning with
// nothing wrong. Alerting on a single gap trains people to ignore the banner,
// which costs more than the gap did. Two consecutive silent days is a feed that
// has actually stopped.
export const SILENT_DAYS_BEFORE_ALERT = 2;

/**
 * Which providers have been silent for the whole check window.
 *
 * Exported and pure so the two easy-to-get-wrong parts are testable: dropping
 * today (its cell is legitimately empty until that night's sync) and requiring
 * a FULL window before alerting (with fewer completed days than the threshold
 * there is not yet evidence of silence, so it must stay quiet).
 */
export function silentProviders(
  rows: DailyIngestRow[],
  providers: readonly string[],
  threshold: number,
): { silent: string[]; checkWindow: string[] } {
  const days = [...new Set(rows.map((r) => r.day))].sort();
  const byKey = new Map(rows.map((r) => [`${r.provider}|${r.day}`, r]));
  const completed = days.slice(0, -1);
  const checkWindow = completed.slice(-threshold);

  if (checkWindow.length < threshold) return { silent: [], checkWindow };

  return {
    silent: providers.filter((p) =>
      checkWindow.every((d) => (byKey.get(`${p}|${d}`)?.files_ok ?? 0) === 0),
    ),
    checkWindow,
  };
}

function fmtDay(iso: string): string {
  return new Date(`${iso}T00:00:00`).toLocaleDateString('en-AU', { day: '2-digit', month: 'short' });
}

function cellClass(row: DailyIngestRow | undefined): string {
  if (!row || row.files_ok === 0) return 'bg-amber-100 border-amber-300 text-amber-800';
  if (row.files_err > 0) return 'bg-emerald-100 border-emerald-300 text-emerald-900 ring-1 ring-inset ring-red-400';
  return 'bg-emerald-100 border-emerald-300 text-emerald-900';
}

function tooltip(provider: string, day: string, row: DailyIngestRow | undefined): string {
  if (!row || row.files_ok === 0) return `${provider} · ${day} — no files`;
  const bits = [`${row.files_ok} file${row.files_ok === 1 ? '' : 's'}`];
  if (row.files_err > 0) bits.push(`${row.files_err} error${row.files_err === 1 ? '' : 's'}`);
  if (row.rows_inserted > 0) bits.push(`${row.rows_inserted.toLocaleString('en-AU')} interval rows`);
  return `${provider} · ${day} — ${bits.join(', ')}`;
}

/** The bv_gcs_daily_ingest rows (migration 098). Throws on failure. */
export async function loadDailyIngest(): Promise<DailyIngestRow[]> {
  const res = await fetch(`${API_BASE}/api/bill-validation/gcs/daily`, {
    headers: { 'x-api-key': 'scopingai' },
  });
  const json = await res.json();
  if (!json.success) throw new Error(json.error || 'Failed to load ingest history');
  return (json.data as DailyIngestRow[]) ?? [];
}

type ProviderIngestMonitorProps = {
  /** From loadDailyIngest — the Dashboard reads them once for this and its summary line. */
  rows: DailyIngestRow[];
  loading: boolean;
  error: string | null;
  className?: string;
};

export const ProviderIngestMonitor = ({ rows, loading, error, className }: ProviderIngestMonitorProps) => {
  // Days come from the response, not from a locally generated range: the view
  // builds its spine in Australia/Sydney and the browser may not be there.
  const days = [...new Set(rows.map((r) => r.day))].sort();
  const byKey = new Map(rows.map((r) => [`${r.provider}|${r.day}`, r]));

  const { silent, checkWindow } = silentProviders(rows, PROVIDERS, SILENT_DAYS_BEFORE_ALERT);
  const completed = days.slice(0, -1);

  const totals = Object.fromEntries(
    PROVIDERS.map((p) => [
      p,
      completed.slice(-7).reduce((acc, d) => acc + (byKey.get(`${p}|${d}`)?.files_ok ?? 0), 0),
    ]),
  ) as Record<string, number>;

  if (loading) {
    return (
      <Card className={className}>
        <CardContent className="p-5">
          <div className="h-4 w-40 bg-gray-100 animate-pulse rounded mb-4" />
          <div className="h-20 bg-gray-100 animate-pulse rounded" />
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className={className}>
        <CardContent className="p-5">
          <p className="text-sm text-red-600">{error}</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={className}>
      <CardHeader className="pb-3">
        <CardTitle className="flex flex-wrap items-center gap-x-3 gap-y-1 text-base">
          <Activity className="h-4 w-4 text-gray-400" />
          Meter file delivery
          <span className="text-xs font-normal text-gray-400">
            files received per day, last {days.length} days
          </span>

          {/* Legend. The colours mean nothing without it — which is also why the
              counts sit inside the cells rather than only in a tooltip. */}
          <span className="ml-auto flex flex-wrap items-center gap-3 text-xs font-normal text-gray-500">
            <span className="flex items-center gap-1">
              <span className="h-3 w-3 rounded-sm border bg-emerald-100 border-emerald-300" /> received
            </span>
            <span className="flex items-center gap-1">
              <span className="h-3 w-3 rounded-sm border bg-amber-100 border-amber-300" /> none
            </span>
            <span className="flex items-center gap-1">
              <span className="h-3 w-3 rounded-sm border bg-emerald-100 border-emerald-300 ring-1 ring-inset ring-red-400" /> had errors
            </span>
          </span>
        </CardTitle>
      </CardHeader>

      <CardContent className="space-y-3">
        {silent.length > 0 && (
          <p className="flex items-center gap-1.5 text-xs text-amber-700">
            <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0" />
            No files from {silent.join(' or ')} for {SILENT_DAYS_BEFORE_ALERT} days
            {' '}({fmtDay(checkWindow[0])} – {fmtDay(checkWindow[checkWindow.length - 1])})
          </p>
        )}

        <div className="overflow-x-auto">
          <table className="min-w-max border-separate border-spacing-0.5">
            <thead>
              <tr>
                <th className="sticky left-0 z-10 bg-white pr-2 text-left text-[11px] font-medium text-gray-400">
                  Provider
                </th>
                {days.map((day) => (
                  <th key={day} className="w-9 text-center text-[10px] font-normal text-gray-400">
                    {new Date(`${day}T00:00:00`).getDate()}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {PROVIDERS.map((provider) => (
                <tr key={provider}>
                  <td className="sticky left-0 z-10 bg-white pr-2 whitespace-nowrap">
                    <span className="text-xs capitalize text-gray-700">{provider}</span>
                    <span className="ml-1.5 text-[11px] text-gray-400">{totals[provider]} / 7d</span>
                  </td>
                  {days.map((day) => {
                    const row = byKey.get(`${provider}|${day}`);
                    return (
                      <td
                        key={day}
                        title={tooltip(provider, fmtDay(day), row)}
                        className={cn(
                          'w-9 rounded-sm border py-1 text-center text-[11px] tabular-nums',
                          cellClass(row),
                        )}
                      >
                        {row?.files_ok ?? 0}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {days.length > 0 && (
          <div className="flex justify-between text-[11px] text-gray-400">
            <span>{fmtDay(days[0])}</span>
            <span>{fmtDay(days[days.length - 1])} (today, partial until tonight&rsquo;s sync)</span>
          </div>
        )}

        {/* processed_at is when our sync ran, not when the provider uploaded —
            prod drains at 06:00 Sydney, so an outage surfaces a day late. Said
            out loud so nobody reads a green cell as "they uploaded that day". */}
        <p className="text-[11px] text-gray-400">
          Bucketed by sync date (06:00 Sydney), not upload time. Alerts only after{' '}
          {SILENT_DAYS_BEFORE_ALERT} consecutive silent days — a single missed day is normal.
        </p>
      </CardContent>
    </Card>
  );
};
